import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileDown, Loader2, Pencil } from "lucide-react";
import { api, errMsg, downloadFile } from "../../lib/api";
import { ddmmyyyy, inr, num, STATUSES } from "../../lib/format";
import { ShareButtons } from "./ShareBox";
import { PhotoThumb } from "./PhotoThumb";
import { POGrns } from "./POGrns";
import { useAuth } from "../../context/AuthContext";

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3 py-1 text-sm">
    <span className="text-muted-foreground">{k}</span>
    <span className="min-w-0 break-words text-right font-medium">{v || "—"}</span>
  </div>
);

const PriceTag = ({ c, testid }) => {
  if (!c) return null;
  const up = c.change_pct > 0, down = c.change_pct < 0;
  const tone = up ? "text-destructive" : down ? "text-brand" : "text-muted-foreground";
  return (
    <p className={`text-xs font-semibold ${tone}`} data-testid={`${testid}-price-check`}>
      {up ? "▲" : down ? "▼" : "="} Last {inr(c.last_rate)} on {c.last_po_no} ({ddmmyyyy(c.last_po_date)})
      {c.change_pct !== 0 ? ` · ${up ? "+" : ""}${c.change_pct}%` : " · same rate"}
    </p>
  );
};

const ItemRow = ({ it, i, testid, check }) => (
  <div className="space-y-2 border-b py-3 last:border-0" data-testid={`${testid}-item-${i}`}>
    <div className="flex justify-between gap-3">
      <p className="min-w-0 font-semibold">{i + 1}. {it.description}{it.colour ? ` (${it.colour})` : ""} {it.code && <span className="mono text-xs text-muted-foreground">[{it.code}]</span>}</p>
      <p className="mono shrink-0 font-bold">{inr(it.total)}</p>
    </div>
    <p className="mono text-xs text-muted-foreground">
      {num(it.qty)} {it.uom} × {inr(it.rate)} = {inr(it.taxable)} + {it.gst}% ({inr(it.tax)})
    </p>
    <PriceTag c={check} testid={`${testid}-item-${i}`} />
    {it.photos?.length > 0 && (
      <div className="grid w-full max-w-[260px] grid-cols-4 gap-2">
        {it.photos.map((p, j) => <PhotoThumb key={p} path={p} testid={`${testid}-item-${i}-photo-${j}`} />)}
      </div>
    )}
  </div>
);

export const PODetail = ({ po, settings, onChanged, testid }) => {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [busy, setBusy] = useState("");
  const [checks, setChecks] = useState({});
  useEffect(() => {
    api.get(`/pos/${po.id}/price-check`).then((r) => setChecks(r.data)).catch(() => setChecks({}));
  }, [po.id, po.updated_at]);
  const v = po.vendor;

  const changeStatus = async (e) => {
    const status = e.target.value;
    setBusy("status");
    try {
      await api.patch(`/pos/${po.id}/status`, { status });
      toast.success(`${po.po_no} marked ${status}`);
      onChanged();
      window.dispatchEvent(new Event("review:changed"));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  const pdf = async () => {
    setBusy("pdf");
    try {
      await downloadFile(`/pos/${po.id}/pdf`, `${po.po_no}.pdf`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="rise space-y-4 border-t bg-muted/20 p-4" data-testid={`${testid}-detail`}>
      <div>
        <h3 className="mono-label mb-1 text-muted-foreground">Vendor</h3>
        <Row k="Name" v={`${v.name} (${v.code})`} />
        <Row k="GSTIN" v={v.gstin} />
        <Row k="Location" v={v.location} />
        <Row k="Payment" v={`${v.payment_days} days from invoice date`} />
        <Row k="Product line" v={po.product_line} />
        <Row k="Expected delivery" v={ddmmyyyy(po.expected_delivery)} />
        <Row k="Deliver to" v={po.deliver_to} />
        <Row k="Raised by" v={po.raised_by} />
      </div>
      <div>
        <h3 className="mono-label mb-1 text-muted-foreground">Items</h3>
        {po.items.map((it, i) => <ItemRow key={it.id} it={it} i={i} testid={testid} check={checks[it.id]} />)}
      </div>
      <div className="rounded-lg bg-card p-3">
        <Row k="Taxable value" v={inr(po.taxable)} />
        <Row k="GST" v={inr(po.tax)} />
        <div className="flex justify-between border-t pt-2 font-bold"><span>Total</span><span className="mono" data-testid={`${testid}-detail-total`}>{inr(po.total)}</span></div>
      </div>
      {po.remarks && <p className="text-sm"><span className="text-muted-foreground">Remarks: </span>{po.remarks}</p>}
      <POGrns po={po} testid={testid} />
      <ShareButtons po={po} settings={settings} testid={`${testid}-share`} />
      <div className="grid grid-cols-2 gap-2">
        <select className="field font-semibold" value={po.status} onChange={changeStatus} disabled={busy === "status" || !can("po_status")} data-testid={`${testid}-status-select`}>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <button className="btn btn-outline" onClick={pdf} disabled={busy === "pdf"} data-testid={`${testid}-pdf-btn`}>
          {busy === "pdf" ? <Loader2 size={18} className="animate-spin" /> : <FileDown size={18} />} PDF
        </button>
      </div>
      {po.status === "Draft" && can("po_write") && (
        <button className="btn btn-outline w-full" onClick={() => navigate(`/pos/${po.id}/edit`)} data-testid={`${testid}-edit-btn`}>
          <Pencil size={18} /> Edit draft
        </button>
      )}
    </div>
  );
};
